import { type FC, useState } from 'react';
import { ProductThumbnail, Text } from '@cencosud-cencommerce/paris-uikit';

import { ProductServiceItem } from '@/Application/Components/ProductList/ProductItem/ProductExtraList/ProductServiceList/ProductServiceItem';
import { Feedback, FeedbackType } from '@/modules/perseo-billing/components';
import {
  FreePill,
  PreSalePill
} from '@/modules/perseo-billing/components/pill/pill.component';
import { type IBasketSummary } from '@/modules/perseo-billing/interfaces';
import type { TBillingOrderConfirmationProductItem } from '@/modules/perseo-billing/interfaces/src/bff-web/billing';
import {
  Divider,
  Icon,
  Typography
} from '@/modules/perseo-billing/ui/react/src/components/beta';

const generateProductAttributes = (
  item: TBillingOrderConfirmationProductItem
) => {
  const quantityInfo = item?.quantity ? `Cantidad: ${item?.quantity}` : '';
  const specificationInfo = item?.specification
    ? `| ${item?.specification}`
    : '';
  const secondHandInfo = item?.isSecondHand ? '| Segunda mano' : '';
  return `${quantityInfo} ${specificationInfo} ${secondHandInfo}`;
};

export const PurchaseItems = ({ delivery }: { delivery: IBasketSummary }) => {
  return (
    <div className="co-flex co-w-full co-flex-col co-gap-16px">
      {delivery?.items?.map(
        (item: TBillingOrderConfirmationProductItem, index: number) => {
          return (
            <LineItem
              item={item}
              isLast={delivery?.items?.length === index + 1}
              key={index}
            />
          );
        }
      )}
    </div>
  );
};

type LineItemProps = {
  item: TBillingOrderConfirmationProductItem;
  isLast: boolean;
};

const LineItem: FC<LineItemProps> = ({ item, isLast }) => {
  const productAttributes = generateProductAttributes(item);

  return (
    <>
      <ItemDetails item={item} productAttributes={productAttributes} />
      {!isLast && <Divider />}
    </>
  );
};

type ItemDetailsProps = {
  item: TBillingOrderConfirmationProductItem;
  productAttributes: string;
};

const ItemDetails: FC<ItemDetailsProps> = ({ item, productAttributes }) => {
  const services = item?.services || [];

  const isMobile = false;
  const isUniqueService = services.length === 1;

  return (
    <>
      <div tabIndex={0} className="co-flex co-flex-col co-gap-1">
        {item?.price?.isColabPrice && (
          <Typography
            weight="semibold"
            color="#6A1BCB"
            className="co-self-end co-text-[#6A1BCB]"
          >
            Descuento colaborador aplicado
          </Typography>
        )}
        {item?.isPresale && <PreSalePill />}
        <div className="co-flex co-gap-3 co-mb-2">
          <ProductThumbnailWithFallback {...item} />
          <div className="co-flex co-gap-2px co-w-full">
            <div className="co-w-full co-flex co-flex-col co-gap-2px">
              {/* Title */}
              <Text variant="body1" weight={600}>
                {item?.title}
              </Text>

              {/* Attributes */}
              {item.isSampling ? (
                <FreePill className="co-flex lg:co-hidden" />
              ) : (
                <Text variant="caption" weight={500}>
                  {productAttributes}
                </Text>
              )}
            </div>
            <div className="co-flex co-gap-8px co-items-center">
              {item?.isSampling && (
                <FreePill className="co-hidden lg:co-flex" />
              )}
              {!item?.isSampling && (
                <>
                  {item?.price?.isCencoPrice && (
                    <Icon icon="CencoCreditCardIcon" width={24} height={24} />
                  )}
                  <Typography weight="medium">{item?.price?.price}</Typography>
                </>
              )}
            </div>
          </div>
        </div>

        {services?.map((service, index) => (
          <ProductServiceItem
            key={index}
            {...service}
            showText={!isMobile || isUniqueService}
          ></ProductServiceItem>
        ))}

        {!!services?.length && (
          <Feedback
            outlined
            type={FeedbackType.INFO}
            subtitle={'Te enviaremos un correo para agendar el servicio.'}
          />
        )}
      </div>
    </>
  );
};

const ProductThumbnailWithFallback = ({
  img,
  title
}: TBillingOrderConfirmationProductItem) => {
  const [hasImageError, setHasImageError] = useState<boolean | undefined>(
    undefined
  );

  return (
    <ProductThumbnail
      variant={hasImageError ? 'icon' : 'default'}
      iconName="image"
      alt={title}
      src={hasImageError ? undefined : img}
      ImageComponent={
        hasImageError === undefined
          ? () => (
              <img
                src={img}
                alt={title}
                width={60}
                height={45}
                className="co-w-[60px] co-h-[45px] co-object-contain co-box-content co-border-1 co-border-[#E6E6E6] co-rounded-4px"
                onError={() => {
                  setHasImageError(true);
                }}
                onLoad={() => {
                  setHasImageError(false);
                }}
              />
            )
          : undefined
      }
    />
  );
};
